import { memo } from 'react'
import styled from 'styled-components'

import { shortcutAppRouter } from '@root/features/shortcut/app-shortcuts'
import { useTranslation } from '@root/shared/localization'

import { PulseModal } from './pulse-modal'

import type { ShortcutRouteHandler } from '@root/features/shortcut/shortcut-router'

const isMac = /mac/i.test(navigator.platform || navigator.userAgent)

/**
 * The keys the app already listens for.
 *
 * Read from the router that binds them rather than written out again here, so
 * a shortcut that is added, changed or dropped cannot leave this list saying
 * something the app no longer does.
 */
export const PulseShortcuts: React.FC<{ onClose: () => void }> = memo(
  ({ onClose }) => {
    const { t } = useTranslation()

    const routes: ReadonlyArray<ShortcutRouteHandler> =
      Object.values(shortcutAppRouter)

    const rows = routes.map((route) => {
      /* Two shapes are allowed: a named pair, or a plain list with windows
         first. The app's own reader tells them apart the same way. */
      const shown =
        'mac' in route.commandShow
          ? (isMac ? route.commandShow.mac : route.commandShow.windows).join(
              ' + ',
            )
          : ((isMac ? route.commandShow[1] : route.commandShow[0]) ?? '')

      return {
        keys: shown
          .split('+')
          .map((key: string) => key.trim())
          .filter(Boolean),
        what: t(route.description),
      }
    })

    return (
      <PulseModal title="Keyboard shortcuts" onClose={onClose} width={440}>
        <Keys>
          {rows.map((row) => (
            <div className="k" key={row.what}>
              <span className="what">{row.what}</span>
              <span className="combo">
                {row.keys.map((key: string) => (
                  <kbd key={key}>{key}</kbd>
                ))}
              </span>
            </div>
          ))}
        </Keys>
      </PulseModal>
    )
  },
)

const Keys = styled.div`
  display: flex;
  flex-direction: column;

  .k {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 40px;
    padding: 0 6px;
    border-bottom: 1px solid var(--line-soft);
  }

  .k:last-child {
    border-bottom: 0;
  }

  .what {
    flex-grow: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--t2);
  }

  .combo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  kbd {
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: var(--chip-hi);
    color: var(--t2);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    font-weight: 500;
  }
`
